import { type DrillRecordIssue, drillEvaluationScore } from '@felix/domain';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Select,
  Stack,
  Textarea,
  TextInput,
  VisuallyHidden,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconChevronLeft,
  IconChevronRight,
  IconDeviceFloppy,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDay } from '../../format.js';
import { DayInput } from '../../inputs/DayInput.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { followLink, useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { OwnerAttachments } from '../pms/OwnerAttachments.js';
import table from '../pms/Pms.module.css';
import work from '../pms/PmsWork.module.css';
import { LoadError } from '../purchasing/table.js';
import { DrillAuditTrail, DrillReportButtons } from './DrillAuditTrail.js';
import { DrillFindingsPanel } from './DrillFindings.js';
import {
  IncidentSelect,
  ReasonDialog,
  RecordDecisionDialog,
  RecordFailure,
  StopDialog,
  SubmitDialog,
} from './DrillRecordDialogs.js';
import { Choice, TimeField, YesNo } from './DrillRecordFields.js';
import classes from './DrillRecords.module.css';
import type { DrillRecordSource } from './record-source.js';
import type { DrillRecord, DrillRecordDetail, DrillRecordState } from './record-types.js';
import {
  draftsOf,
  formatSeconds,
  type RecordDrafts,
  STAGE_LETTER,
  stageIssues,
  stagePayload,
  WIZARD_STAGES,
  type WizardStage,
} from './record-view.js';

const REST_HOURS = '/crew/rest-hours';
const HSEQ = '/compliance/hseq';

const STATE_TONE: Record<DrillRecordState, StatusTone> = {
  in_progress: 'info',
  submitted: 'warning',
  approved: 'success',
  stopped: 'danger',
  cancelled: 'neutral',
};

export function RecordStateBadge({
  state,
  returned,
  reopened,
}: {
  state: DrillRecordState;
  returned?: boolean;
  reopened?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <StatusBadge tone={STATE_TONE[state]}>{t(`drillRecord.state.${state}`)}</StatusBadge>
      {state === 'in_progress' && returned ? (
        <StatusBadge tone="danger">{t('drillRecord.state.returned')}</StatusBadge>
      ) : null}
      {state === 'in_progress' && reopened ? (
        <StatusBadge tone="warning">{t('drillRecord.state.reopened')}</StatusBadge>
      ) : null}
    </>
  );
}

type Dialog = 'submit' | 'decide' | 'reopen' | 'cancel' | 'stop' | null;

/**
 * Execution Wizard (ortağın belgesi §5; ADR 0128 Faz 3 Ek): A General, B Preparation,
 * C Participants, D Execution, E Evaluation, F Close. Kayıt süren hâlde (In Progress) aşama aşama
 * ara kayıtla dolar; eksiksiz kayıt kaptana gönderilir, kaptan onaylar ya da iade eder. Onaylı
 * kayıt değişmez; düzeltme kaptanın gerekçeli yeniden açmasıyla. Ofiste salt okunur.
 */
export function DrillRecordWizard({
  id,
  source,
  stage,
  onStage,
  onBack,
}: {
  id: string;
  source: DrillRecordSource;
  stage: WizardStage;
  onStage: (stage: WizardStage) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; code: string }
    | { kind: 'ready'; record: DrillRecordDetail }
  >({ kind: 'loading' });
  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', record: await source.detail(id) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, id]);
  useEffect(() => {
    void load();
  }, [load]);
  if (state.kind === 'error') {
    return (
      <Panel>
        <LoadError
          code={state.code}
          title={t('drillRecord.wizard.error')}
          onRetry={() => void load()}
        />
      </Panel>
    );
  }
  if (state.kind === 'loading') {
    return (
      <Panel>
        <p className={classes.muted}>{t('drillRecord.wizard.loading')}</p>
      </Panel>
    );
  }
  // Her yeni sürümde taslaklar kayıttan yeniden kurulur
  return (
    <WizardContent
      key={`${state.record.rowVersion}-${state.record.updatedAt}-${state.record.events.length}`}
      record={state.record}
      source={source}
      stage={stage}
      onStage={onStage}
      onBack={onBack}
      reload={load}
    />
  );
}

/** Yüklenmiş kaydın sihirbazı (ekran testleri doğrudan çizer). */
export function WizardContent({
  record,
  source,
  stage,
  onStage,
  onBack,
  reload,
}: {
  record: DrillRecordDetail;
  source: DrillRecordSource;
  stage: WizardStage;
  onStage: (stage: WizardStage) => void;
  onBack: () => void;
  reload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillRecord.wizard.${key}`, values);
  const saved = useMemo(() => draftsOf(record), [record]);
  const [drafts, setDraftState] = useState<RecordDrafts>(saved);
  const setDrafts = useCallback(
    (update: (drafts: RecordDrafts) => RecordDrafts) => setDraftState((current) => update(current)),
    [],
  );
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [checked, setChecked] = useState(false);
  const load = reload;
  const editable =
    record.side === 'ship' &&
    record.state === 'in_progress' &&
    record.can.edit &&
    Boolean(source.save);
  const dirty = (which: WizardStage) =>
    which !== 'close' &&
    JSON.stringify(stagePayload(which, drafts)) !== JSON.stringify(stagePayload(which, saved));

  /** Aşamayı kaydeder (değişiklik varsa); başarılıysa `true`. */
  const save = async (which: WizardStage): Promise<boolean> => {
    if (!editable || !dirty(which) || which === 'close' || !source.save) return true;
    setSaving(true);
    setFailure(null);
    try {
      await source.save({
        id: record.id,
        rowVersion: record.rowVersion,
        ...(stagePayload(which, drafts) as Record<string, never>),
      });
      await load();
      return true;
    } catch (error) {
      setFailure(error);
      return false;
    } finally {
      setSaving(false);
    }
  };
  const go = async (next: WizardStage) => {
    if (next === stage) return;
    if (await save(stage)) onStage(next);
  };
  const index = WIZARD_STAGES.indexOf(stage);
  const prev = WIZARD_STAGES[index - 1];
  const next = WIZARD_STAGES[index + 1];
  const done = async (value: DrillRecord) => {
    setDialog(null);
    await load();
    if (value.state !== 'in_progress') setChecked(false);
  };
  const fieldError = (stageName: WizardStage, field: string) => {
    if (!checked) return undefined;
    const issue = stageIssues(record.issues, stageName).find((i) => i.field === field);
    return issue
      ? t(`drillRecord.issue.${issue.code}`, { defaultValue: t('drillPlan.fieldErrors.REQUIRED') })
      : undefined;
  };
  const readOnly = !editable;
  const canDecide =
    record.side === 'ship' &&
    record.state === 'submitted' &&
    (record.can.approve || record.can.return) &&
    Boolean(source.decide);

  return (
    <Stack gap="md">
      <Panel>
        <div className={classes.head}>
          <div className={classes.titleBlock}>
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={14} aria-hidden />}
              onClick={() => void save(stage).then((ok) => ok && onBack())}
              style={{ alignSelf: 'flex-start' }}
            >
              {k('back')}
            </Button>
            <span className={classes.recordNo}>{record.recordNo}</span>
            <h2 className={classes.title}>{`${record.templateCode} · ${record.templateName}`}</h2>
            <div className={classes.meta}>
              <span>{record.vesselName ?? '—'}</span>
              <span>{formatDay(record.general.drillDate)}</span>
              <span>
                {record.libraryVersion !== record.templateVersion
                  ? k('versionPinned', {
                      version: record.templateVersion,
                      library: record.libraryVersion,
                    })
                  : k('version', { version: record.templateVersion })}
              </span>
              {record.variantDecisionId ? <span>{k('vesselScenario')}</span> : null}
            </div>
          </div>
          <div className={classes.badges}>
            <RecordStateBadge
              state={record.state}
              returned={record.returned}
              reopened={record.reopened}
            />
            {record.auditTrail ? <DrillReportButtons record={record} source={source} /> : null}
            {editable && source.stop ? (
              <Button
                color="red"
                variant="light"
                leftSection={<IconAlertTriangle size={16} aria-hidden />}
                onClick={() => setDialog('stop')}
              >
                {t('drillRecord.actions.stop')}
              </Button>
            ) : null}
          </div>
        </div>
      </Panel>

      {record.state === 'in_progress' &&
      (record.returned || record.reopened) &&
      record.lastReason ? (
        <Notice
          color={record.returned ? 'red' : 'yellow'}
          title={k(record.returned ? 'returned' : 'reopened')}
        >
          {record.lastReason}
        </Notice>
      ) : null}
      {canDecide ? (
        <Notice color="yellow" title={k('awaitingMaster')}>
          <Button onClick={() => setDialog('decide')}>{t('drillRecord.actions.decide')}</Button>
        </Notice>
      ) : record.state === 'submitted' ? (
        <Alert color="yellow">{k('submittedNote')}</Alert>
      ) : null}
      {record.stop ? <StoppedPanel record={record} source={source} onSaved={done} /> : null}
      {record.state === 'cancelled' && record.cancelReason ? (
        <Notice color="gray" title={k('cancelled')}>
          {record.cancelReason}
        </Notice>
      ) : null}

      <nav aria-label={k('stages')}>
        <ol className={classes.stages}>
          {WIZARD_STAGES.map((s) => {
            const open = stageIssues(record.issues, s).length;
            return (
              <li key={s}>
                <button
                  type="button"
                  className={classes.stage}
                  aria-current={s === stage ? 'step' : undefined}
                  data-done={s !== 'close' && open === 0 ? '' : undefined}
                  onClick={() => void go(s)}
                >
                  <span className={classes.letter} aria-hidden>
                    {STAGE_LETTER[s]}
                  </span>
                  <span className={classes.stageText}>
                    {t(`drillRecord.stages.${s}`)}
                    {s === 'close' ? null : open > 0 ? (
                      <span className={classes.stageCount}>{k('open', { count: open })}</span>
                    ) : (
                      <span className={classes.stageOk}>{k('complete')}</span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <Panel>
        <Stack gap="md">
          <h3 className={classes.sectionTitle}>
            {`${STAGE_LETTER[stage]}. ${t(`drillRecord.stages.${stage}`)}`}
          </h3>
          {stage === 'general' ? (
            <GeneralStage
              record={record}
              drafts={drafts}
              setDrafts={setDrafts}
              readOnly={readOnly}
              fieldError={(f) => fieldError('general', f)}
            />
          ) : null}
          {stage === 'preparation' ? (
            <PreparationStage
              record={record}
              drafts={drafts}
              setDrafts={setDrafts}
              readOnly={readOnly}
              fieldError={(f) => fieldError('preparation', f)}
            />
          ) : null}
          {stage === 'participants' ? (
            <ParticipantsStage
              record={record}
              drafts={drafts}
              setDrafts={setDrafts}
              readOnly={readOnly}
              fieldError={(f) => fieldError('participants', f)}
            />
          ) : null}
          {stage === 'execution' ? (
            <ExecutionStage
              record={record}
              drafts={drafts}
              setDrafts={setDrafts}
              readOnly={readOnly}
              source={source}
              fieldError={(f) => fieldError('execution', f)}
              onError={setFailure}
            />
          ) : null}
          {stage === 'evaluation' ? (
            <EvaluationStage
              record={record}
              drafts={drafts}
              setDrafts={setDrafts}
              readOnly={readOnly}
              fieldError={(f) => fieldError('evaluation', f)}
            />
          ) : null}
          {stage === 'close' ? (
            <CloseStage
              record={record}
              source={source}
              reload={load}
              onGo={(s) => {
                setChecked(true);
                void go(s);
              }}
              onDialog={(d) => {
                setChecked(true);
                setDialog(d);
              }}
            />
          ) : null}
          <RecordFailure error={failure} />
          <div className={classes.footer}>
            <div className={classes.footerGroup}>
              {prev ? (
                <Button
                  variant="default"
                  size="md"
                  leftSection={<IconChevronLeft size={16} aria-hidden />}
                  onClick={() => void go(prev)}
                  loading={saving}
                >
                  {k('previous')}
                </Button>
              ) : null}
            </div>
            <div className={classes.footerGroup}>
              {editable && stage !== 'close' ? (
                <Button
                  variant="light"
                  size="md"
                  leftSection={<IconDeviceFloppy size={16} aria-hidden />}
                  disabled={!dirty(stage)}
                  loading={saving}
                  onClick={() => void save(stage)}
                >
                  {k('save')}
                </Button>
              ) : null}
              {next ? (
                <Button
                  size="md"
                  rightSection={<IconChevronRight size={16} aria-hidden />}
                  onClick={() => void go(next)}
                  loading={saving}
                >
                  {editable && dirty(stage) ? k('saveNext') : k('next')}
                </Button>
              ) : null}
            </div>
          </div>
        </Stack>
      </Panel>

      <SubmitDialog
        record={dialog === 'submit' ? record : null}
        source={source}
        onClose={() => setDialog(null)}
        onSaved={(value) => void done(value)}
      />
      <RecordDecisionDialog
        record={dialog === 'decide' ? record : null}
        source={source}
        onClose={() => setDialog(null)}
        onSaved={(value) => void done(value)}
      />
      <ReasonDialog
        record={dialog === 'reopen' || dialog === 'cancel' ? record : null}
        kind={dialog === 'cancel' ? 'cancel' : 'reopen'}
        source={source}
        onClose={() => setDialog(null)}
        onSaved={(value) => void done(value)}
      />
      <StopDialog
        record={dialog === 'stop' ? record : null}
        source={source}
        onClose={() => setDialog(null)}
        onSaved={(value) => void done(value)}
      />
    </Stack>
  );
}

interface StageProps {
  record: DrillRecordDetail;
  drafts: RecordDrafts;
  setDrafts: (update: (drafts: RecordDrafts) => RecordDrafts) => void;
  readOnly: boolean;
  fieldError: (field: string) => string | undefined;
}

/* ---------- A. General Information ---------- */

function GeneralStage({ record, drafts, setDrafts, readOnly, fieldError }: StageProps) {
  const { t } = useTranslation();
  const f = (key: string) => t(`drillRecord.general.${key}`);
  const g = drafts.general;
  const set = (patch: Partial<RecordDrafts['general']>) =>
    setDrafts((d) => ({ ...d, general: { ...d.general, ...patch } }));
  const crew = record.crewOnBoard.map((c) => ({
    value: c.userId,
    label: c.rank ? `${c.name} · ${c.rank}` : c.name,
  }));
  return (
    <div className={classes.grid3}>
      <TextInput
        label={f('drillType')}
        readOnly
        value={`${record.templateCode} · ${record.templateName}`}
        size="md"
      />
      <TextInput label={f('category')} readOnly value={record.categoryName ?? '—'} size="md" />
      <DayInput
        label={f('date')}
        required
        size="md"
        readOnly={readOnly}
        value={g.drillDate}
        onChange={(value) => set({ drillDate: value ?? g.drillDate })}
        error={fieldError('drillDate')}
      />
      <TimeField
        label={f('startTime')}
        required
        value={g.startTime}
        onChange={(value) => set({ startTime: value })}
        tzOffsetMin={record.tzOffsetMin}
        readOnly={readOnly}
        error={fieldError('startTime')}
      />
      <TimeField
        label={f('endTime')}
        required
        value={g.endTime}
        onChange={(value) => set({ endTime: value })}
        tzOffsetMin={record.tzOffsetMin}
        readOnly={readOnly}
        error={fieldError('endTime')}
      />
      <TextInput
        label={f('duration')}
        readOnly
        size="md"
        value={
          record.durationMinutes === null
            ? '—'
            : t('drillRecord.minutes', { count: record.durationMinutes })
        }
      />
      <TextInput
        className={classes.full}
        label={f('location')}
        description={f('locationHint')}
        required
        size="md"
        readOnly={readOnly}
        value={g.location}
        onChange={(event) => set({ location: event.currentTarget.value })}
        error={fieldError('location')}
      />
      <Select
        label={f('responsible')}
        required
        searchable
        size="md"
        readOnly={readOnly}
        data={crew}
        value={g.responsibleUserId}
        onChange={(value) => set({ responsibleUserId: value })}
        error={fieldError('responsibleUserId')}
      />
      <Textarea
        className={classes.full}
        label={f('scenario')}
        description={f('scenarioHint')}
        required
        autosize
        minRows={3}
        readOnly={readOnly}
        value={g.scenario}
        onChange={(event) => set({ scenario: event.currentTarget.value })}
        error={fieldError('scenario')}
      />
    </div>
  );
}

/* ---------- B. Preparation ---------- */

function PreparationStage({ record, drafts, setDrafts, readOnly, fieldError }: StageProps) {
  const { t } = useTranslation();
  const f = (key: string) => t(`drillRecord.preparation.${key}`);
  const p = drafts.preparation;
  const set = (patch: Partial<RecordDrafts['preparation']>) =>
    setDrafts((d) => ({ ...d, preparation: { ...d.preparation, ...patch } }));
  return (
    <Stack gap="md">
      <div className={classes.grid2}>
        <Select
          label={f('ra')}
          description={f('raHint')}
          clearable
          searchable
          size="md"
          readOnly={readOnly}
          nothingFoundMessage={f('noRa')}
          data={record.raVerifications.map((r) => ({
            value: r.id,
            label: `${r.number} · ${r.title}${r.date ? ` · ${formatDay(r.date)}` : ''}`,
          }))}
          value={p.raVerificationId}
          onChange={(value) => set({ raVerificationId: value })}
        />
        <Textarea
          label={f('riskNotes')}
          autosize
          minRows={1}
          readOnly={readOnly}
          value={p.riskNotes}
          onChange={(event) => set({ riskNotes: event.currentTarget.value })}
        />
        <YesNo
          label={f('briefingDone')}
          value={p.briefingDone}
          onChange={(value) => set({ briefingDone: value })}
          readOnly={readOnly}
          error={fieldError('briefingDone')}
        />
        <Textarea
          label={f('briefingNotes')}
          autosize
          minRows={1}
          readOnly={readOnly}
          value={p.briefingNotes}
          onChange={(event) => set({ briefingNotes: event.currentTarget.value })}
        />
        <YesNo
          label={f('equipmentReady')}
          value={p.equipmentReady}
          onChange={(value) => set({ equipmentReady: value })}
          readOnly={readOnly}
          error={fieldError('equipmentReady')}
        />
        <Textarea
          label={f('equipmentNotes')}
          description={p.equipmentReady === false ? f('equipmentNotesHint') : undefined}
          autosize
          minRows={1}
          readOnly={readOnly}
          value={p.equipmentNotes}
          onChange={(event) => set({ equipmentNotes: event.currentTarget.value })}
          error={fieldError('equipmentNotes')}
        />
        <TextInput
          label={f('weather')}
          size="md"
          readOnly={readOnly}
          value={p.weather}
          onChange={(event) => set({ weather: event.currentTarget.value })}
        />
        <Textarea
          label={f('constraints')}
          description={f('constraintsHint')}
          autosize
          minRows={1}
          readOnly={readOnly}
          value={p.constraints}
          onChange={(event) => set({ constraints: event.currentTarget.value })}
        />
      </div>
      {record.content.equipment.length > 0 ? (
        <p className={classes.hint}>
          {`${f('equipment')}: ${record.content.equipment.join(', ')}`}
        </p>
      ) : null}
      {p.checklist.length > 0 ? (
        <section>
          <h4 className={classes.sectionTitle}>{f('checklist')}</h4>
          <ul className={classes.checklist}>
            {p.checklist.map((item, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: liste sabit (şablonun hazırlık maddeleri)
              <li key={`${index}-${item.item}`}>
                <Checkbox
                  size="md"
                  label={item.item}
                  checked={item.done}
                  disabled={readOnly}
                  onChange={(event) => {
                    const checked = event.currentTarget.checked;
                    set({
                      checklist: p.checklist.map((c, i) =>
                        i === index ? { ...c, done: checked } : c,
                      ),
                    });
                  }}
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </Stack>
  );
}

/* ---------- C. Participants ---------- */

function ParticipantsStage({ record, drafts, setDrafts, readOnly, fieldError }: StageProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const f = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillRecord.participants.${key}`, values);
  const [adding, setAdding] = useState<string | null>(null);
  const people = drafts.participants;
  const setPeople = (next: RecordDrafts['participants']) =>
    setDrafts((d) => ({ ...d, participants: next }));
  const roles = [
    ...new Set(
      [...record.content.roles.map((r) => r.role), ...people.map((p) => p.role)].filter(Boolean),
    ),
  ];
  const restRow = new Map(record.participants.map((p) => [p.userId ?? p.id, p.rest]));
  const gaps = record.participants.filter(
    (p) => p.attendance === 'present' && p.rest !== null && p.rest !== 'covered',
  ).length;
  const notListed = record.crewOnBoard.filter((c) => !people.some((p) => p.userId === c.userId));
  const present = people.filter((p) => p.attendance === 'present').length;
  return (
    <Stack gap="md">
      <div className={classes.meta}>
        <span>{f('summary', { present, total: people.length })}</span>
      </div>
      {gaps > 0 ? (
        <Notice color="yellow" title={f('restTitle')}>
          <Stack gap={6}>
            <span>{f('restBody', { count: gaps })}</span>
            {shell ? (
              <a
                href={REST_HOURS}
                onClick={(event) => followLink(event, REST_HOURS, shell.navigate)}
              >
                {f('openRest')}
              </a>
            ) : null}
          </Stack>
        </Notice>
      ) : null}
      <section
        className={table.tableBox}
        aria-label={t('drillRecord.stages.participants')}
        // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
        tabIndex={0}
      >
        <table className={table.table} data-cards>
          <thead>
            <tr>
              <th scope="col">{f('name')}</th>
              <th scope="col">{f('role')}</th>
              <th scope="col">{f('attendance')}</th>
              <th scope="col">{f('absenceReason')}</th>
              <th scope="col">{f('rest')}</th>
              {readOnly ? null : (
                <th scope="col">
                  <VisuallyHidden>{f('remove')}</VisuallyHidden>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {people.map((person, index) => {
              const update = (patch: Partial<(typeof people)[number]>) =>
                setPeople(people.map((p, i) => (i === index ? { ...p, ...patch } : p)));
              const rest = person.id ? restRow.get(person.userId ?? person.id) : null;
              return (
                <tr key={person.id ?? `new-${person.userId ?? person.name}`}>
                  <td data-label={f('name')}>
                    <span className={classes.rowName}>
                      <strong>{person.name}</strong>
                      <span className={classes.muted}>{person.rank ?? '—'}</span>
                    </span>
                  </td>
                  <td data-label={f('role')}>
                    <Autocomplete
                      aria-label={`${f('role')}: ${person.name}`}
                      size="md"
                      readOnly={readOnly}
                      data={roles}
                      value={person.role}
                      onChange={(value) => update({ role: value })}
                    />
                  </td>
                  <td data-label={f('attendance')}>
                    <Choice
                      label={`${f('attendance')}: ${person.name}`}
                      hideLabel
                      value={person.attendance}
                      readOnly={readOnly}
                      options={[
                        { value: 'present', label: f('present') },
                        { value: 'absent', label: f('absent') },
                      ]}
                      onChange={(value) => update({ attendance: value })}
                      error={fieldError(`${index}.attendance`)}
                    />
                  </td>
                  <td data-label={f('absenceReason')}>
                    <TextInput
                      aria-label={`${f('absenceReason')}: ${person.name}`}
                      size="md"
                      readOnly={readOnly}
                      disabled={person.attendance !== 'absent'}
                      placeholder={f('absenceHint')}
                      value={person.attendance === 'absent' ? person.absenceReason : ''}
                      onChange={(event) => update({ absenceReason: event.currentTarget.value })}
                      error={fieldError(`${index}.absenceReason`)}
                    />
                  </td>
                  <td data-label={f('rest')}>
                    {rest ? (
                      <StatusBadge
                        tone={
                          rest === 'covered' ? 'success' : rest === 'partial' ? 'warning' : 'danger'
                        }
                      >
                        {f(`restState.${rest}`)}
                      </StatusBadge>
                    ) : (
                      '—'
                    )}
                  </td>
                  {readOnly ? null : (
                    <td data-label={f('remove')}>
                      <Button
                        variant="subtle"
                        color="red"
                        size="md"
                        aria-label={`${f('remove')}: ${person.name}`}
                        onClick={() => setPeople(people.filter((_, i) => i !== index))}
                      >
                        <IconTrash size={18} aria-hidden />
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      {readOnly ? null : (
        <div className={classes.grid2}>
          <Select
            label={f('add')}
            description={f('addHint')}
            searchable
            clearable
            size="md"
            nothingFoundMessage={f('allListed')}
            data={notListed.map((c) => ({
              value: c.userId,
              label: c.rank ? `${c.name} · ${c.rank}` : c.name,
            }))}
            value={adding}
            onChange={setAdding}
          />
          <Button
            size="md"
            variant="light"
            leftSection={<IconPlus size={16} aria-hidden />}
            style={{ alignSelf: 'end' }}
            disabled={!adding}
            onClick={() => {
              const person = record.crewOnBoard.find((c) => c.userId === adding);
              if (!person) return;
              setPeople([
                ...people,
                {
                  id: null,
                  rowVersion: null,
                  userId: person.userId,
                  name: person.name,
                  rank: person.rank,
                  role: '',
                  attendance: null,
                  absenceReason: '',
                },
              ]);
              setAdding(null);
            }}
          >
            {f('addButton')}
          </Button>
        </div>
      )}
    </Stack>
  );
}

/* ---------- D. Execution ---------- */

function ExecutionStage({
  record,
  drafts,
  setDrafts,
  readOnly,
  source,
  fieldError,
  onError,
}: StageProps & { source: DrillRecordSource; onError: (error: unknown) => void }) {
  const { t } = useTranslation();
  const f = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillRecord.execution.${key}`, values);
  const crew = record.crewOnBoard.map((c) => ({ value: c.userId, label: c.name }));
  const results = [
    { value: 'completed' as const, label: f('completed') },
    { value: 'not_completed' as const, label: f('notCompleted') },
    { value: 'na' as const, label: f('na') },
  ];
  return (
    <Stack gap="sm">
      <p className={classes.hint}>{f('hint')}</p>
      <ol className={classes.stepList}>
        {record.steps.map((step, index) => {
          const draft = drafts.steps[index];
          if (!draft) return null;
          const update = (patch: Partial<typeof draft>) =>
            setDrafts((d) => ({
              ...d,
              steps: d.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)),
            }));
          return (
            <li key={step.id} className={classes.stepCard} data-result={draft.result ?? undefined}>
              <span className={classes.stepNo} aria-hidden>
                {step.stepNo}
              </span>
              <div className={classes.stepBody}>
                <p className={classes.stepText}>{step.text}</p>
                <div className={classes.stepFields}>
                  <Choice
                    label={`${f('result')}: ${step.stepNo}`}
                    hideLabel
                    value={draft.result}
                    readOnly={readOnly}
                    options={results}
                    onChange={(value) => update({ result: value })}
                    error={fieldError(`${step.stepNo}.result`)}
                  />
                  <div>
                    <TimeField
                      label={f('time')}
                      value={draft.doneTime}
                      onChange={(value) => update({ doneTime: value })}
                      tzOffsetMin={record.tzOffsetMin}
                      readOnly={readOnly}
                    />
                    {step.elapsedSeconds !== null ? (
                      <span className={classes.elapsed}>
                        {f('elapsed', { time: formatSeconds(step.elapsedSeconds) })}
                      </span>
                    ) : null}
                  </div>
                  <Select
                    label={f('responsible')}
                    clearable
                    searchable
                    size="md"
                    readOnly={readOnly}
                    data={crew}
                    value={draft.responsibleUserId}
                    onChange={(value) => update({ responsibleUserId: value })}
                  />
                </div>
                <Textarea
                  label={f('remarks')}
                  description={draft.result === 'not_completed' ? f('remarksHint') : undefined}
                  autosize
                  minRows={1}
                  readOnly={readOnly}
                  value={draft.remarks}
                  onChange={(event) => update({ remarks: event.currentTarget.value })}
                  error={fieldError(`${step.stepNo}.remarks`)}
                />
                {source.attachments ? (
                  <OwnerAttachments
                    ownerId={step.id}
                    list={source.attachments}
                    upload={readOnly ? undefined : source.upload}
                    href={source.attachmentUrl}
                    emptyText={f('noEvidence')}
                    addLabel={f('addEvidence')}
                    label={f('evidence', { no: step.stepNo })}
                    onError={onError}
                  />
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </Stack>
  );
}

/* ---------- E. Evaluation ---------- */

function EvaluationStage({ record, drafts, setDrafts, readOnly, fieldError }: StageProps) {
  const { t } = useTranslation();
  const f = (key: string) => t(`drillRecord.evaluation.${key}`);
  const set = (key: string, patch: Partial<RecordDrafts['evaluation']['results'][string]>) =>
    setDrafts((d) => ({
      ...d,
      evaluation: {
        ...d.evaluation,
        results: {
          ...d.evaluation.results,
          [key]: {
            ...(d.evaluation.results[key] ?? { rating: null, seconds: '', note: '' }),
            ...patch,
          },
        },
      },
    }));
  return (
    <Stack gap="sm">
      <p className={classes.hint}>{f('hint')}</p>
      <div>
        {record.criteria.map((criterion) => {
          const value = drafts.evaluation.results[criterion.key] ?? {
            rating: null,
            seconds: '',
            note: '',
          };
          return (
            <div key={criterion.key} className={classes.criterion}>
              <span className={classes.criterionLabel}>{criterion.label}</span>
              {criterion.kind === 'time' ? (
                <TextInput
                  label={f('measured')}
                  description={f('measuredHint')}
                  placeholder="m:ss"
                  inputMode="numeric"
                  size="md"
                  readOnly={readOnly}
                  value={value.seconds}
                  onChange={(event) => set(criterion.key, { seconds: event.currentTarget.value })}
                />
              ) : (
                <Choice
                  label={`${criterion.label}: ${f('rating')}`}
                  hideLabel
                  value={value.rating}
                  readOnly={readOnly}
                  options={[
                    { value: 'satisfactory', label: f('satisfactory') },
                    { value: 'needs_improvement', label: f('needsImprovement') },
                  ]}
                  onChange={(rating) => set(criterion.key, { rating })}
                  error={fieldError(criterion.key)}
                />
              )}
              <TextInput
                label={f('note')}
                size="md"
                readOnly={readOnly}
                value={value.note}
                onChange={(event) => set(criterion.key, { note: event.currentTarget.value })}
              />
            </div>
          );
        })}
      </div>
      <EvaluationScore record={record} drafts={drafts} />
      <CrewEvaluation
        drafts={drafts}
        setDrafts={setDrafts}
        readOnly={readOnly}
        fieldError={fieldError}
      />
      <Textarea
        label={f('debriefing')}
        description={record.content.debriefing.join(' · ') || undefined}
        autosize
        minRows={3}
        readOnly={readOnly}
        value={drafts.evaluation.debriefing}
        onChange={(event) => {
          const debriefing = event.currentTarget.value;
          setDrafts((d) => ({ ...d, evaluation: { ...d.evaluation, debriefing } }));
        }}
      />
    </Stack>
  );
}

/** Şirket içi puan (belge §6): Satisfactory oranı; mevzuat zorunluluğu gibi sunulmaz. */
function EvaluationScore({ record, drafts }: Pick<StageProps, 'record' | 'drafts'>) {
  const { t } = useTranslation();
  const score = drillEvaluationScore(
    record.criteria,
    Object.entries(drafts.evaluation.results).map(([key, value]) => ({
      key,
      rating: value.rating,
    })),
  );
  if (score.percent === null) return null;
  return (
    <p className={classes.hint} data-testid="drill-score">
      {t('drillFinding.evaluation.score', {
        percent: score.percent,
        satisfactory: score.satisfactory,
        rated: score.rated,
      })}{' '}
      <span className={classes.muted}>{t('drillFinding.evaluation.scoreNote')}</span>
    </p>
  );
}

/**
 * Kişi değerlendirmesi (Crew Competency Matrix; Faz 4 Ek): yalnız katılan (present) kişiler;
 * Needs Improvement gözlemi yazılır. Eğitim ihtiyacı bulgu olarak (Close) açılır.
 */
function CrewEvaluation({
  drafts,
  setDrafts,
  readOnly,
  fieldError,
}: Pick<StageProps, 'drafts' | 'setDrafts' | 'readOnly' | 'fieldError'>) {
  const { t } = useTranslation();
  const f = (key: string) => t(`drillRecord.evaluation.${key}`);
  const c = (key: string) => t(`drillFinding.evaluation.${key}`);
  const present = drafts.participants.filter((p) => p.attendance === 'present' && p.userId);
  const set = (userId: string, patch: Partial<RecordDrafts['evaluation']['crew'][string]>) =>
    setDrafts((d) => ({
      ...d,
      evaluation: {
        ...d.evaluation,
        crew: {
          ...d.evaluation.crew,
          [userId]: { ...(d.evaluation.crew[userId] ?? { rating: null, note: '' }), ...patch },
        },
      },
    }));
  return (
    <section aria-labelledby="drill-crew-evaluation">
      <h3 id="drill-crew-evaluation" className={classes.sectionTitle}>
        {c('crewTitle')}
      </h3>
      <p className={classes.hint}>{c('crewHint')}</p>
      {present.length === 0 ? (
        <p className={classes.muted}>{c('nonepresent')}</p>
      ) : (
        present.map((person) => {
          const userId = String(person.userId);
          const value = drafts.evaluation.crew[userId] ?? { rating: null, note: '' };
          return (
            <div key={userId} className={classes.criterion}>
              <span className={classes.criterionLabel}>
                {person.name}
                {person.rank ? <span className={classes.muted}> · {person.rank}</span> : null}
              </span>
              <Choice
                label={`${person.name}: ${c('rating')}`}
                hideLabel
                value={value.rating}
                readOnly={readOnly}
                options={[
                  { value: 'satisfactory', label: f('satisfactory') },
                  { value: 'needs_improvement', label: f('needsImprovement') },
                ]}
                onChange={(rating) => set(userId, { rating })}
                error={fieldError(`crew.${userId}`)}
              />
              <TextInput
                label={c('note')}
                size="md"
                readOnly={readOnly}
                value={value.note}
                error={fieldError(`crew.${userId}.note`)}
                onChange={(event) => set(userId, { note: event.currentTarget.value })}
              />
            </div>
          );
        })
      )}
    </section>
  );
}

/* ---------- F. Close ---------- */

const ISSUE_STAGE: Record<DrillRecordIssue['stage'], WizardStage> = {
  general: 'general',
  preparation: 'preparation',
  participants: 'participants',
  execution: 'execution',
  evaluation: 'evaluation',
};

function CloseStage({
  record,
  source,
  reload,
  onGo,
  onDialog,
}: {
  record: DrillRecordDetail;
  source: DrillRecordSource;
  reload: () => Promise<void>;
  onGo: (stage: WizardStage) => void;
  onDialog: (dialog: Exclude<Dialog, null>) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillRecord.close.${key}`, values);
  const ship = record.side === 'ship';
  const present = record.participants.filter((p) => p.attendance === 'present').length;
  const count = (result: string) => record.steps.filter((s) => s.result === result).length;
  const issueText = (issue: DrillRecordIssue) => {
    const parts = issue.field.split('.');
    // Kişi değerlendirmesi: `crew.<userId>[.note]` (Faz 4 Ek)
    if (issue.stage === 'evaluation' && parts[0] === 'crew') {
      const person = record.participants.find((p) => p.userId === parts[1])?.name ?? '';
      const what = parts[2]
        ? t(`drillRecord.issueField.${parts[2]}`, { defaultValue: parts[2] })
        : null;
      return `${t('drillRecord.stages.evaluation')}: ${[
        t('drillFinding.evaluation.crewTitle'),
        person,
        what,
      ]
        .filter(Boolean)
        .join(' · ')} — ${t(`drillRecord.issue.${issue.code}`, {
        defaultValue: t('drillPlan.fieldErrors.REQUIRED'),
      })}`;
    }
    const [first, rest] = parts;
    const where =
      issue.stage === 'participants'
        ? (record.participants[Number(first)]?.name ?? '')
        : issue.stage === 'execution'
          ? f('step', { no: first })
          : issue.stage === 'evaluation'
            ? (record.criteria.find((c) => c.key === first)?.label ?? first)
            : t(`drillRecord.${issue.stage}.${issue.field}`);
    const field = rest ? t(`drillRecord.issueField.${rest}`, { defaultValue: rest }) : null;
    const label = [where, field].filter(Boolean).join(' · ');
    return `${t(`drillRecord.stages.${issue.stage}`)}${label ? `: ${label}` : ''} — ${t(
      `drillRecord.issue.${issue.code}`,
      { defaultValue: t('drillPlan.fieldErrors.REQUIRED') },
    )}`;
  };
  return (
    <Stack gap="md">
      <dl className={work.facts}>
        {(
          [
            [f('drill'), `${record.templateCode} · ${record.templateName}`],
            [f('date'), formatDay(record.general.drillDate)],
            [
              f('time'),
              record.general.startTime
                ? `${record.general.startTime}–${record.general.endTime ?? '…'}`
                : '—',
            ],
            [f('responsible'), record.general.responsibleName ?? '—'],
            [
              f('participants'),
              f('participantsValue', { present, total: record.participants.length }),
            ],
            [
              f('steps'),
              f('stepsValue', {
                completed: count('completed'),
                notCompleted: count('not_completed'),
                na: count('na'),
                total: record.steps.length,
              }),
            ],
          ] as [string, string][]
        ).map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {record.state === 'in_progress' ? (
        record.issues.length === 0 ? (
          <Alert color="green">{f('ready')}</Alert>
        ) : (
          <Notice color="yellow" title={f('missing', { count: record.issues.length })}>
            <ul className={classes.issues}>
              {record.issues.map((issue) => (
                <li key={`${issue.stage}-${issue.field}-${issue.code}`}>
                  <button
                    type="button"
                    className={classes.issueLink}
                    onClick={() => onGo(ISSUE_STAGE[issue.stage])}
                  >
                    {issueText(issue)}
                  </button>
                </li>
              ))}
            </ul>
          </Notice>
        )
      ) : null}
      {ship ? (
        <div className={classes.footerGroup}>
          {record.state === 'in_progress' && record.can.submit && source.submit ? (
            <Button
              size="md"
              disabled={record.issues.length > 0}
              onClick={() => onDialog('submit')}
            >
              {t('drillRecord.actions.submit')}
            </Button>
          ) : null}
          {record.state === 'submitted' &&
          (record.can.approve || record.can.return) &&
          source.decide ? (
            <Button size="md" onClick={() => onDialog('decide')}>
              {t('drillRecord.actions.decide')}
            </Button>
          ) : null}
          {record.state === 'approved' && record.can.return && source.reopen ? (
            <Button size="md" variant="default" onClick={() => onDialog('reopen')}>
              {t('drillRecord.actions.reopen')}
            </Button>
          ) : null}
          {record.state === 'in_progress' && record.can.edit && source.cancel ? (
            <Button size="md" variant="subtle" color="red" onClick={() => onDialog('cancel')}>
              {t('drillRecord.actions.cancel')}
            </Button>
          ) : null}
        </div>
      ) : null}
      <p className={classes.hint}>{f('reportNote')}</p>
      {record.findings ? (
        <DrillFindingsPanel record={record} source={source} reload={reload} />
      ) : null}
      {record.auditTrail ? (
        <DrillAuditTrail record={record} />
      ) : (
        <section>
          <h4 className={classes.sectionTitle}>{f('history')}</h4>
          {record.events.length === 0 ? (
            <p className={classes.muted}>{f('noHistory')}</p>
          ) : (
            <ol className={classes.history}>
              {record.events.map((event) => (
                <li key={event.id}>
                  <strong>{t(`drillRecord.event.${event.kind}`)}</strong>
                  {` · ${formatDateTime(event.at)} · ${event.byName ?? '—'}`}
                  {event.capacity === 'deputy' ? ` (${t('drillPlan.onBehalf')})` : ''}
                  {event.reason ? <div>{event.reason}</div> : null}
                  {event.decision ? (
                    <div>
                      {`${t(`drillRecord.decisionValue.${event.decision.decision}`)} · ${formatDateTime(
                        event.decision.decidedAt,
                      )} · ${event.decision.decidedByName ?? '—'}${
                        event.decision.capacity === 'deputy' ? ` (${t('drillPlan.onBehalf')})` : ''
                      }`}
                      {event.decision.reason ? ` — ${event.decision.reason}` : ''}
                    </div>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </section>
      )}
    </Stack>
  );
}

/* ---------- Gerçek acil durum ---------- */

function StoppedPanel({
  record,
  source,
  onSaved,
}: {
  record: DrillRecordDetail;
  source: DrillRecordSource;
  onSaved: (record: DrillRecord) => void;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  const f = (key: string, values: Record<string, unknown> = {}) =>
    t(`drillRecord.stop.${key}`, values);
  const [incident, setIncident] = useState<string | null>(record.stop?.hseReportId ?? null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  if (!record.stop) return null;
  const canLink =
    record.side === 'ship' && record.state === 'stopped' && record.can.edit && source.linkIncident;
  const linked = record.incidents.find((i) => i.id === record.stop?.hseReportId);
  return (
    <Notice color="red" title={f('stoppedTitle', { time: record.stop.stoppedTime ?? '—' })}>
      <Stack gap="xs">
        <span>{record.stop.reason}</span>
        <span className={classes.muted}>
          {linked ? f('linked', { no: linked.number, title: linked.title }) : f('notLinked')}
        </span>
        {canLink ? (
          <div className={classes.grid2}>
            <IncidentSelect record={record} value={incident} onChange={setIncident} />
            <Button
              style={{ alignSelf: 'end' }}
              size="md"
              variant="light"
              loading={busy}
              disabled={!incident || incident === record.stop.hseReportId}
              onClick={async () => {
                if (!incident) return;
                setBusy(true);
                setFailure(null);
                try {
                  const saved = await source.linkIncident?.({
                    id: record.id,
                    rowVersion: record.rowVersion,
                    hseReportId: incident,
                  });
                  if (saved) onSaved(saved);
                } catch (error) {
                  setFailure(error);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {f('link')}
            </Button>
          </div>
        ) : null}
        {shell ? (
          <a href={HSEQ} onClick={(event) => followLink(event, HSEQ, shell.navigate)}>
            {f('openHseq')}
          </a>
        ) : null}
        <RecordFailure error={failure} />
      </Stack>
    </Notice>
  );
}

/** Başlıklı uyarı: Mantine başlığının açık zeminde kontrastı yetmez; başlık lacivert (WCAG 1.4.3). */
function Notice({
  color,
  title,
  children,
}: {
  color: string;
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <Alert color={color}>
      <Stack gap={6}>
        <strong className={classes.noticeTitle}>{title}</strong>
        {children}
      </Stack>
    </Alert>
  );
}
